import React, { useMemo, useState } from 'react';
import { H } from './quantum/gates';
import { zeroState, basisState, applyGate, densityMatrixFromState, blochVector } from './quantum/QuantumState';
import { bitFlipChannel, phaseFlipChannel, depolarizingChannel, dephasingChannel, applyReadoutError } from './quantum/noise';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';

const PRESET_STATES = {
  '0': { label: '|0⟩', state: zeroState(1) },
  '1': { label: '|1⟩', state: basisState(1, 1) },
  '+': { label: '|+⟩', state: applyGate(zeroState(1), H, 0, 1) },
};

const CHANNELS = {
  bitflip: { label: 'Bit-flip', fn: bitFlipChannel, max: 0.5, desc: 'With probability p, an X gate accidentally fires, flipping |0⟩↔|1⟩.' },
  phaseflip: { label: 'Phase-flip', fn: phaseFlipChannel, max: 0.5, desc: 'With probability p, a Z gate accidentally fires, flipping the relative phase.' },
  depolarizing: { label: 'Depolarizing', fn: depolarizingChannel, max: 1, desc: 'With probability p, the qubit is replaced entirely by the fully-mixed state I/2 — the generic "total noise" model.' },
  dephasing: { label: 'Dephasing (decoherence)', fn: dephasingChannel, max: 1, desc: 'Coherence (the x,y Bloch components) decays toward zero while the population (z) is untouched — the textbook signature of decoherence.' },
};

export default function NoiseLab() {
  const [presetId, setPresetId] = useState('+');
  const [channelId, setChannelId] = useState('depolarizing');
  const [strength, setStrength] = useState(0.3);
  const [readoutShots, setReadoutShots] = useState(null);

  const isReadout = channelId === 'readout';
  const channel = CHANNELS[channelId] || null;
  const clampedStrength = channel ? Math.min(strength, channel.max) : strength;

  const idealState = PRESET_STATES[presetId].state;
  const idealRho = useMemo(() => densityMatrixFromState(idealState), [idealState]);
  const idealVector = blochVector(idealRho);
  const noisyRho = useMemo(() => (channel ? channel.fn(idealRho, clampedStrength) : idealRho), [idealRho, channel, clampedStrength]);
  const noisyVector = blochVector(noisyRho);

  const idealP0 = idealRho[0][0].re * 100;
  const noisyP0 = noisyRho[0][0].re * 100;

  const changeChannel = (id) => {
    setChannelId(id);
    if (CHANNELS[id]) setStrength(Math.min(strength, CHANNELS[id].max));
    setReadoutShots(null);
  };

  const runReadoutComparison = () => {
    const p0 = idealRho[0][0].re;
    const shots = 500;
    const ideal = [0, 0];
    const noisy = [0, 0];
    for (let i = 0; i < shots; i++) {
      const outcome = Math.random() < p0 ? 0 : 1;
      ideal[outcome]++;
      noisy[applyReadoutError(outcome, clampedStrength)]++;
    }
    setReadoutShots({ ideal, noisy, shots });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Noise Lab</h1>
          <p className="text-gray-600">Real quantum hardware is imperfect. Dial in a noise strength and watch the Bloch vector — and the measurement statistics — degrade.</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Ideal qubit</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(PRESET_STATES).map(([id, { label }]) => (
              <button
                key={id}
                onClick={() => setPresetId(id)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${presetId === id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Noise channel</h2>
          <div className="flex flex-wrap gap-2 mb-2">
            {Object.entries(CHANNELS).map(([id, c]) => (
              <button
                key={id}
                onClick={() => changeChannel(id)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${channelId === id ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-purple-300'}`}
              >
                {c.label}
              </button>
            ))}
            <button
              onClick={() => changeChannel('readout')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${isReadout ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-purple-300'}`}
            >
              Measurement / readout error
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {isReadout
              ? "With probability p, the reported measurement bit is flipped after the fact — the qubit's own quantum state is never touched."
              : channel.desc}
          </p>

          <div className="mt-3">
            <label className="text-sm text-gray-600 block mb-1">
              Noise strength: {(clampedStrength * 100).toFixed(0)}%
              {!isReadout && channel.max < 1 && <span className="text-gray-400"> (capped at {channel.max * 100}% — beyond that it would start "un-flipping")</span>}
            </label>
            <input
              type="range"
              min="0"
              max={isReadout ? 1 : channel.max}
              step="0.01"
              value={isReadout ? strength : clampedStrength}
              onChange={(e) => { setStrength(parseFloat(e.target.value)); setReadoutShots(null); }}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {isReadout ? (
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide text-center">Ideal vs noisy measurement (500 shots)</h2>
            <div className="flex justify-center mb-3">
              <button onClick={runReadoutComparison} className="px-5 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm">
                ▶ Run 500 shots
              </button>
            </div>
            {readoutShots ? (
              <div className="grid grid-cols-2 gap-6 max-w-md mx-auto">
                <div>
                  <div className="text-xs font-bold text-gray-500 text-center mb-1">IDEAL RESULT</div>
                  {[0, 1].map((b) => (
                    <div key={b} className="flex items-center gap-2 text-xs mb-1">
                      <span className="font-mono w-6">|{b}⟩</span>
                      <div className="flex-1 bg-gray-100 rounded h-4 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-blue-500" style={{ width: `${(readoutShots.ideal[b] / readoutShots.shots) * 100}%` }} />
                      </div>
                      <span className="w-10 text-right">{((readoutShots.ideal[b] / readoutShots.shots) * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 text-center mb-1">NOISY RESULT</div>
                  {[0, 1].map((b) => (
                    <div key={b} className="flex items-center gap-2 text-xs mb-1">
                      <span className="font-mono w-6">|{b}⟩</span>
                      <div className="flex-1 bg-gray-100 rounded h-4 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-red-500" style={{ width: `${(readoutShots.noisy[b] / readoutShots.shots) * 100}%` }} />
                      </div>
                      <span className="w-10 text-right">{((readoutShots.noisy[b] / readoutShots.shots) * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm italic text-center">Run the shots to compare.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Ideal result</h3>
              <div className="flex justify-center"><BlochSphereCanvas vector={idealVector} size={180} showMixed={false} /></div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs max-w-[220px] mx-auto">
                <div className="bg-cyan-50 rounded p-1 border border-cyan-100"><div className="text-cyan-600">P(|0⟩)</div><div className="font-bold text-cyan-700">{idealP0.toFixed(0)}%</div></div>
                <div className="bg-pink-50 rounded p-1 border border-pink-100"><div className="text-pink-600">P(|1⟩)</div><div className="font-bold text-pink-700">{(100 - idealP0).toFixed(0)}%</div></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Noisy result</h3>
              <div className="flex justify-center"><BlochSphereCanvas vector={noisyVector} size={180} /></div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs max-w-[220px] mx-auto">
                <div className="bg-cyan-50 rounded p-1 border border-cyan-100"><div className="text-cyan-600">P(|0⟩)</div><div className="font-bold text-cyan-700">{noisyP0.toFixed(0)}%</div></div>
                <div className="bg-pink-50 rounded p-1 border border-pink-100"><div className="text-pink-600">P(|1⟩)</div><div className="font-bold text-pink-700">{(100 - noisyP0).toFixed(0)}%</div></div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Bloch vector length: {Math.hypot(noisyVector.x, noisyVector.y, noisyVector.z).toFixed(2)} (1.0 = pure, 0.0 = fully mixed)</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
